import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { createContext } from "use-context-selector";
import Loader from "../component/Loader";
import { AnimatePresence, motion } from "framer-motion";
import { isMobile } from "react-device-detect";

const ThemeContext = createContext();
export default ThemeContext;

// Cache version - change this to bust cache
const CSS_VERSION = "v2";

// Track loaded CSS files to avoid reloading
const loadedCssCache = new Set();

// Helper to get SKY route-specific CSS file
const getSkyRouteCssFile = (route) => {
  if (["exchange"].includes(route)) {
    return "/assets/Sky/web.css";
  } else {
    return isMobile ? "/assets/Sky/sky.css" : "/assets/Sky/web.css";
  }
};

// CSS file configurations for each route group
const CSS_CONFIG = {
  DEFAULT_DARK: [
    isMobile ? "/assets/darkStyle.css" : "/assets/WebDark.css",
    // Listing
    isMobile ? "/assets/listing/listing.css" : "/assets/listing/listingWeb.css",
    "/assets/listing/listing2.css",
    "/assets/listing/listing3.css",
    "/assets/listing/listingFilter.css",
    "/assets/listing/bookmaker.css",
    // Search
    "/assets/css/search.css",
    // Desktop only
    !isMobile && "/assets/css/desktop.css",
  ].filter(Boolean),

  // DEFAULT_LIGHT: [
  //   isMobile ? "/assets/index.css" : "/assets/WebIndex.css",
  //   isMobile && "/assets/Main.css",
  //   // Listing
  //   isMobile ? "/assets/listing/listing.css" : "/assets/listing/listingWeb.css",
  //   "/assets/listing/listing2.css",
  //   "/assets/listing/listing3.css",
  //   "/assets/listing/listingFilter.css",
  //   "/assets/listing/bookmaker.css",
  //   // Search
  //   "/assets/css/search.css",
  //   // Desktop only
  //   !isMobile && "/assets/css/desktop.css",
  // ].filter(Boolean),

  VELKI: [
    "/assets/velkie/velkie.css",
    "/assets/velkie/velkieMedia.css",
    "/assets/icomoon.css",
    // "/assets/velkie/calendar.css",
  ],
 SPIN: ["/assets/Spin.css", "/assets/SpinWheel/SpinWheel.css"],
  SKY: ["/assets/icomoon.css"],
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setTheme] = useState("dark");
  const [cssLoaded, setCssLoaded] = useState(false);
  const prevRouteGroupRef = useRef(null);
  const prevRouteRef = useRef(null);
  const prevThemeRef = useRef("dark");
  const isInitialMount = useRef(true);

  let light = isMobile
    ? "s-default is-mobile"
    : "s-default is-desktop canvas webgl supports csstransforms csstransforms3d avif apng webp no-jpeg2000 webp-alpha webp-animation webp-lossless";
  let dark = isMobile
    ? "is-mobile s-dark canvas webgl supports csstransforms csstransforms3d avif apng webp no-jpeg2000 webp-alpha webp-animation webp-lossless"
    : "s-dark is-desktop canvas webgl supports csstransforms csstransforms3d avif apng webp no-jpeg2000 webp-alpha webp-animation webp-lossless";
  const htmlClassname = dark;
  const bodyClassname = "E28-theme-dark";

  const getRouteGroup = useCallback((pathname) => {
    const route = pathname?.split("/")[1];

    if (
      [
        "sports-velki",
        "match-detail-velki",
        "league-velki",
        "favourite-velkie",
        "exchange-velki",
        "menu",
      ].includes(route)
    ) {
      return "VELKI";
    }

    if (
      [
        "sports-sky",
        "match-detail-sky",
        "inplay-sky",
        "horse-racing-sky",
        "favourite-sky",
        "sports-sky-new",
        "account-sky",
        "exchange",
        "result-sky",
      ].includes(route)
    ) {
      return "SKY";
    }

    if (["spin-wheel"].includes(route)) {
      return "SPIN";
    }

    return "DEFAULT";
  }, []);

  const addStylesheet = useCallback((href) => {
    return new Promise((resolve) => {
      const fullHref = `${href}?${CSS_VERSION}`;

      // Check if already loaded
      if (loadedCssCache.has(fullHref)) {
        return resolve();
      }

      // Check if link already exists in DOM
      const existingLink = document.querySelector(`link[href="${fullHref}"]`);
      if (existingLink) {
        loadedCssCache.add(fullHref);
        return resolve();
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fullHref;
      link.setAttribute("data-theme", "dynamic");

      link.onload = () => {
        loadedCssCache.add(fullHref);
        resolve();
      };

      link.onerror = () => {
        console.warn(`⚠️ CSS failed to load: ${href}`);
        // Resolve anyway to not block rendering
        resolve();
      };

      document.head.appendChild(link);
    });
  }, []);

  useEffect(() => {
    const loadCss = async () => {
      const currentGroup = getRouteGroup(location.pathname);
      const currentRoute = location.pathname?.split("/")[1];
      const themeChanged = prevThemeRef.current !== theme;

      // Check if we need to reload CSS for SKY group route changes
      let needsSkyCssReload = false;
      if (
        currentGroup === "SKY" &&
        prevRouteGroupRef.current === "SKY" &&
        !isInitialMount.current
      ) {
        const prevSkyCss = getSkyRouteCssFile(prevRouteRef.current);
        const currentSkyCss = getSkyRouteCssFile(currentRoute);
        needsSkyCssReload = prevSkyCss !== currentSkyCss;
      }

      const groupChanged = prevRouteGroupRef.current !== currentGroup;

      // Skip if nothing changed
      if (
        !groupChanged &&
        !themeChanged &&
        !isInitialMount.current &&
        !needsSkyCssReload
      ) {
        return;
      }

      isInitialMount.current = false;
      const prevGroup = prevRouteGroupRef.current;
      prevRouteGroupRef.current = currentGroup;
      prevRouteRef.current = currentRoute;
      prevThemeRef.current = theme;

      setCssLoaded(false);

      try {
        // Cleanup previous stylesheets when switching groups, theme, or SKY route
        if (prevGroup !== currentGroup || themeChanged || needsSkyCssReload) {
          document
            .querySelectorAll('link[data-theme="dynamic"]')
            .forEach((link) => {
              const href = link.getAttribute("href");
              // For SKY route changes, remove old SKY CSS files
              if (needsSkyCssReload && href?.includes("/assets/Sky/")) {
                link.remove();
                loadedCssCache.delete(href);
              }
              // For group or theme changes, remove all dynamic CSS
              if (prevGroup !== currentGroup || themeChanged) {
                link.remove();
                loadedCssCache.delete(href);
              }
            });
        }

        // Apply body/html classes based on group
        if (currentGroup === "VELKI") {
          if (!isMobile) document.body.className = "is-desktop";
          document.documentElement.className =
            "canvas webgl supports csstransforms csstransforms3d avif apng webp s-webp no-jpeg2000 webp-animation webp-alpha webp-lossless";
          document.documentElement.style.setProperty(
            "--root-font-size",
            isMobile ? "3.7333333333333334vw" : "17.92px",
          );
        } else if (currentGroup === "SKY") {
          if (isMobile) document.body.className = "overfloxScroll";
        } else {
          // DEFAULT group
          document.documentElement.className = htmlClassname;
          document.body.className = bodyClassname;
          localStorage.setItem("theme", theme);
        }

        // Get CSS files for current group
        let cssFiles = [];

        if (currentGroup === "DEFAULT") {
          cssFiles = [
            ...(theme === "dark"
              ? CSS_CONFIG.DEFAULT_DARK
              : CSS_CONFIG.DEFAULT_LIGHT),
          ];
        } else {
          cssFiles = [...(CSS_CONFIG[currentGroup] || [])];
        }

        // Add conditional CSS for VELKI exchange route
        if (currentGroup === "VELKI") {
          if (["exchange"].includes(currentRoute)) {
            cssFiles = [
              "/assets/Sky/web.css",
              "/assets/icomoon.css",
            ];
          }
        }

        // Add conditional CSS for SKY
        if (currentGroup === "SKY") {
          if (["exchange"].includes(currentRoute)) {
            cssFiles.push("/assets/Sky/web.css");
          } else {
            cssFiles.push(
              isMobile ? "/assets/Sky/sky.css" : "/assets/Sky/web.css",
            );
          }
          if (["result-sky"].includes(currentRoute) && isMobile) {
            cssFiles.push("/assets/Sky/result.css");
          }
        }

        await Promise.all(cssFiles.map(addStylesheet));

        // Small delay to let styles paint
        await new Promise((resolve) => setTimeout(resolve, 100));

        setCssLoaded(true);
      } catch (error) {
        console.error("CSS loading error:", error);
        setCssLoaded(true);
      }
    };

    loadCss();
  }, [theme, location.pathname, getRouteGroup, addStylesheet]);

  const toggleTheme = () => {
    // setCssLoaded(false);
    // setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {![
        "sports-velki",
        "match-detail-velki",
        "league-velki",
        "sports-sky",
        "inplay-sky",
        "match-detail-sky",
        "horse-racing-sky",
        "favourite-velkie",
        "favourite-sky",
        "sports-sky-new",
        "account-sky",
        "exchange",
        "exchange-velki",
      ].includes(location.pathname?.split("/")[1]) && (
        <AnimatePresence mode="wait">
          {!cssLoaded && (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-white"
            >
              <Loader />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* ✅ children not keyed → no remount/double render */}
      {cssLoaded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      )}
    </ThemeContext.Provider>
  );
};
